import { InvestorDetailsGetInvestorLegalEntitiesQuery, PaginateSortDirection } from '@/__generated__/graphql';
import { DownloadItemIcon } from '@/assets/static/icons/download-item';
import Button from '@/components/basicComponents/button';
import { ISelectValue } from '@/components/basicComponents/select';
import { ITab } from '@/components/basicComponents/tabs';
import { Loader } from '@/components/loaders/loader';
import { DOCUMENT_FIELD_RENDER_ORDER_QUERY, PUBLISHED_INVESTMENT_MANAGER_UPDATES_QUERY } from '@/components/managerUpdates-page/queries';
import PublishedUpdatesTabDownload from '@/components/managerUpdates-page/tabs/publishedUpdatesTab/modals/publishedUpdatesTabDownload';
import { PublishedUpdatesTable } from '@/components/managerUpdates-page/tabs/publishedUpdatesTab/table/table';
import PublishedUpdatesMobileTable from '@/components/managerUpdates-page/tabs/publishedUpdatesTab/table/tableMobile';
import { PublishedInvestmentManagerUpdatesDto, PublishedInvestmentManagerUpdatesSourceEnum } from '@/components/managerUpdates-page/types';
import { TableColumns } from '@/components/table/types';
import { useResponsive } from '@/hooks/use-responsive';
import { useSearchParamsManager } from '@/hooks/use-search-params';
import { PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { useQuery } from '@apollo/client';
import { LegalEntityGroupingLevel } from '@trace-8/trace8-schema';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { InvestorDetailsLayout } from '../../investorDetailsLayout';
import { InvestorDetailsEntityTypeEnum } from '../../types';

const legalEntityGroupingLevelMap = {
  [InvestorDetailsEntityTypeEnum.Household]: LegalEntityGroupingLevel.Household,
  [InvestorDetailsEntityTypeEnum.Group]: LegalEntityGroupingLevel.Group,
  [InvestorDetailsEntityTypeEnum.UngroupedEntity]: LegalEntityGroupingLevel.Entity,
  [InvestorDetailsEntityTypeEnum.GroupedEntity]: LegalEntityGroupingLevel.Entity
};

interface UpdatesTabProps {
  entityType: InvestorDetailsEntityTypeEnum;
  investorData: {
    investorName: string;
    advisorName: string;
    legalEntities: InvestorDetailsGetInvestorLegalEntitiesQuery['getInvestor']['legalEntities'];
    legalEntitiesTotalCount: number;
  };
  selectedGroupName: string;
  entitiesSelectOptions: ISelectValue[];
  groupId: string | null;
  tabs: ITab[];
  resetEntityType: () => void;
}

export const UpdatesTab = ({
  entityType,
  investorData,
  selectedGroupName,
  entitiesSelectOptions,
  groupId,
  tabs,
  resetEntityType
}: UpdatesTabProps) => {
  const { isMobile } = useResponsive();
  const navigate = useNavigate();
  const params = useParams();
  const { searchParams, saveScrollPostion, makeScroll, onChangeSort } = useSearchParamsManager();

  const investorId = params?.investorId;
  const entityId = params?.entityId;
  const position = searchParams.get('position') || 0;
  const key = searchParams.get('key') || 'STRATEGY';
  const asc = searchParams.get('asc') === null || searchParams.get('asc') === 'true';

  const [publishedUpdatesTableColumns, setPublishedUpdatesTableColumns] = useState<TableColumns[]>([]);
  const [publishedUpdates, setPublishedUpdates] = useState<PublishedInvestmentManagerUpdatesDto[]>([]);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });

  const { loading: documentFieldRenderOrderLoading } = useQuery(DOCUMENT_FIELD_RENDER_ORDER_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (data) => {
      const updatedTableColumns: TableColumns[] = data?.documentFieldRenderOrder?.map((column, index) => ({
        title: column.name,
        key: null as string,
        keySort: column.name,
        width: index === 0 ? 277 : 200
      }));

      setPublishedUpdatesTableColumns([
        { title: 'Strategy', key: 'STRATEGY', width: 400, keySort: 'strategy', isFixed: true },
        ...updatedTableColumns,
        { title: 'Last Update', key: null, width: 200, keySort: 'last update' }
      ]);
    }
  });

  const { loading: publishedInvestmentManagerUpdatesLoading } = useQuery(PUBLISHED_INVESTMENT_MANAGER_UPDATES_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !entityType,
    variables: {
      data: {
        familyId: investorId,
        legalEntityGroupingLevel: legalEntityGroupingLevelMap[entityType],
        legalEntityGroupingLevelId: entityType === InvestorDetailsEntityTypeEnum.Group ? groupId : entityId,
        sortDirection: asc ? PaginateSortDirection.Asc : PaginateSortDirection.Desc,
        publishedUpdatesOnly: true
      }
    },
    onCompleted: (data) => {
      setPublishedUpdates(data?.publishedInvestmentManagerUpdates || []);
      makeScroll(position);
    }
  });

  const openOpportunityDetailsPage = (investmentId: string) => {
    saveScrollPostion();
    navigate(`/strategyDetails/${investmentId}?tab=Updates&backToTitle=Investor Entity`);
  };

  const getFileName = () => {
    switch (entityType) {
      case InvestorDetailsEntityTypeEnum.Household:
        return investorData?.investorName || '';
      case InvestorDetailsEntityTypeEnum.Group:
        return entitiesSelectOptions.find((option) => option.value === `group-${entityId}`)?.label || '';
      case InvestorDetailsEntityTypeEnum.GroupedEntity:
        const selectedGroupedEntityName = entitiesSelectOptions.find((option) => option.value === entityId)?.label || '';
        return `${selectedGroupName} ${selectedGroupedEntityName}`;
      case InvestorDetailsEntityTypeEnum.UngroupedEntity:
        const selectedUngroupedEntityName = entitiesSelectOptions.find((option) => option.value === entityId)?.label || '';
        return selectedUngroupedEntityName;
      default:
        return '';
    }
  };

  const openModalWindow = (type: string) => {
    setModalWindow({ isOpen: true, type });
  };

  const closeModalWindow = () => {
    setModalWindow({ isOpen: false, type: '' });
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'download' && (
        <PublishedUpdatesTabDownload
          isOpen={modalWindow.isOpen}
          publishedUpdatesTableColumns={publishedUpdatesTableColumns}
          publishedUpdates={publishedUpdates}
          fileName={getFileName()}
          source={PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB}
          onClose={closeModalWindow}
        />
      )}
      <InvestorDetailsLayout
        entityType={entityType}
        investorData={investorData}
        selectedGroupName={selectedGroupName}
        entitiesSelectOptions={entitiesSelectOptions}
        tabs={tabs}
        resetEntityType={resetEntityType}
      >
        {documentFieldRenderOrderLoading ? (
          <Loader />
        ) : (
          <PublishedUpdatesTabWrapper>
            {isMobile ? (
              <>
                {publishedInvestmentManagerUpdatesLoading ? (
                  <Loader />
                ) : (
                  <PublishedUpdatesMobileTable
                    columns={publishedUpdatesTableColumns}
                    publishedUpdates={publishedUpdates}
                    source={PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB}
                    openOpportunityDetailsPage={openOpportunityDetailsPage}
                  />
                )}
              </>
            ) : (
              <PublishedUpdatesTable
                columns={publishedUpdatesTableColumns}
                publishedUpdates={publishedUpdates}
                sort={{ key, asc }}
                loading={publishedInvestmentManagerUpdatesLoading}
                source={PublishedInvestmentManagerUpdatesSourceEnum.UPDATES_TAB}
                setSort={onChangeSort}
                openOpportunityDetailsPage={openOpportunityDetailsPage}
              />
            )}
            {publishedUpdates.length > 0 && (
              <DownloadButtonWrapper>
                <Button onClick={() => openModalWindow('download')} size="lg" icon={<DownloadItemIcon fill={themeVar('context.light')} />}>
                  Download
                </Button>
              </DownloadButtonWrapper>
            )}
          </PublishedUpdatesTabWrapper>
        )}
      </InvestorDetailsLayout>
    </>
  );
};

const PublishedUpdatesTabWrapper = styled(PaddingWrap)`
  padding-top: 50px;
  padding-bottom: 40px;
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 30px;
`;
